/**
 * ADR-0156 §10, second owner revision (2026-08-03) — status-first, per the
 * prior-art survey (`atlas-meta/ai/RESEARCH/20260803_140500-backup-admin-ux-prior-art.md`):
 * backup is a property with a status, not a configuration page. One verdict
 * line; setup/change is a stepped flow with verification inside it; failures
 * collapse to a destination-level banner (the platform-wide destination means
 * tenants cannot individually "disconnect"), and only the rare partial
 * divergence — the tampering alarm — names individual trees.
 */

import { useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { useToast } from "../components/Toast.js";
import {
  classifyIncident,
  endpointForm,
  endpointLooksValid,
  failingSince,
  formatTimestamp,
  summarizeTrees,
  syncStatus,
  tenantLabel,
  treeLabel,
  truncateError,
} from "../lib/backupDisplay.js";

// Mirrors the hub's GET /admin/backup shape (ADR-0156 §9).
type BackupDestination = {
  id: string;
  kind: "atlas_box";
  name: string;
  endpoint: string;
  hostKeyFingerprint: string | null;
  status: "unverified" | "verified" | "error";
  statusDetail: string | null;
  verifiedAt: string | null;
  keyPresent: boolean;
};

type BackupTreeRow = {
  tenantName: string | null;
  tree: string;
  firstPushAt: string | null;
  lastSuccessAt: string | null;
  lastAttemptAt: string | null;
  inSync: boolean;
  stale: boolean;
  lastError: string | null;
};

type BackupOverview = {
  destination: BackupDestination | null;
  gate: { writesEnabled: boolean; posture: "refuse"; reason: string | null };
  trees: BackupTreeRow[];
};

type FlowStep =
  | { step: "edit" }
  | { step: "connecting" }
  | { step: "fingerprint"; fingerprint: string; hostKeyLine: string };

export function AdminBackup() {
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<BackupOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // The setup/change flow (§10 rev 2: verification lives INSIDE the flow).
  const [flowOpen, setFlowOpen] = useState(false);
  const [flow, setFlow] = useState<FlowStep>({ step: "edit" });
  const [endpoint, setEndpoint] = useState("");
  const [endpointError, setEndpointError] = useState<string | null>(null);
  const [flowError, setFlowError] = useState<string | null>(null);
  // After Remove, offer the last-known destination back instead of an empty field.
  const [lastEndpoint, setLastEndpoint] = useState<string>("");

  async function load(opts?: { silent?: boolean }) {
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<BackupOverview>("/api/admin/backup");
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function openFlow(prefill: string) {
    setEndpoint(prefill);
    setEndpointError(null);
    setFlowError(null);
    setFlow({ step: "edit" });
    setFlowOpen(true);
  }

  function validateEndpoint() {
    if (endpoint.trim().length === 0) {
      setEndpointError(null);
      return;
    }
    setEndpointError(endpointLooksValid(endpoint) ? null : "That doesn't look like a reachable destination.");
  }

  /** Save (if needed) + verify; a 409 pauses at the fingerprint-confirm step. */
  async function connect(confirmHostKey?: string) {
    setFlow({ step: "connecting" });
    setFlowError(null);
    setBusy(true);
    try {
      if (!confirmHostKey) {
        await apiFetch("/api/admin/backup/destination", { method: "POST", body: { endpoint: endpoint.trim() } });
      }
      await apiFetch("/api/admin/backup/destination/verify", {
        method: "POST",
        body: confirmHostKey ? { confirmHostKey } : {},
      });
      setFlowOpen(false);
      toast.push({ kind: "success", message: "Backups are on." });
      await load({ silent: true });
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "host_key_unconfirmed") {
        setFlow({
          step: "fingerprint",
          fingerprint: String(e.data.fingerprint ?? ""),
          hostKeyLine: String(e.data.hostKeyLine ?? ""),
        });
      } else {
        setFlow({ step: "edit" });
        setFlowError(e instanceof Error ? e.message : String(e));
        await load({ silent: true });
      }
    } finally {
      setBusy(false);
    }
  }

  async function syncNow() {
    setBusy(true);
    try {
      const res = await apiFetch<{ enqueued: number }>("/api/admin/backup/sync", { method: "POST" });
      toast.push({
        kind: "success",
        message:
          res.enqueued === 0
            ? "Everything is in sync — nothing to push."
            : `Sync started — ${res.enqueued} tree${res.enqueued === 1 ? "" : "s"} behind.`,
      });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    const ok = await confirm.confirm({
      title: "Remove backup destination?",
      detail:
        "Every tree and document write will refuse until backups are set up again. " +
        "The box keeps its copies; only the hub's destination record is removed.",
      confirmLabel: "Remove destination",
    });
    if (!ok) return;
    setBusy(true);
    try {
      if (destination) setLastEndpoint(destination.endpoint);
      await apiFetch("/api/admin/backup/destination", { method: "DELETE" });
      toast.push({ kind: "success", message: "Destination removed — writes now refuse." });
      setFlowOpen(false);
      await load({ silent: true });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const destination = data?.destination ?? null;
  const trees = data?.trees ?? [];
  const summary = summarizeTrees(trees);
  const incident = classifyIncident(summary);
  const backupsOn = destination?.status === "verified";
  const who =
    summary.tenantCount > 0
      ? `${summary.tenantCount} tenant${summary.tenantCount === 1 ? "" : "s"}${summary.hasPlatform ? " + platform" : ""}`
      : "platform";

  return (
    <PageLayout title="Backup">
      <div style={{ display: "grid", gap: 12 }}>
        {error && <ErrorState detail={error} />}
        {loading && <LoadingState />}

        {!loading && !error && !flowOpen && (
          <div className="card">
            {/* ── The verdict ─────────────────────────────────────────────── */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {!backupsOn ? (
                <span className="pill pillBad">Backups: off</span>
              ) : incident.kind === "destination" ? (
                <span className="pill pillBad">Backups: failing</span>
              ) : incident.kind === "partial" ? (
                <span className="pill pillWarn">Backups: attention</span>
              ) : (
                <span className="pill pillOk">Backups: on</span>
              )}
              {backupsOn && (
                <button className="btn btnSm" type="button" disabled={busy} onClick={() => void syncNow()}>
                  Sync now
                </button>
              )}
            </div>

            {/* ── The sentence under the verdict ──────────────────────────── */}
            <div style={{ marginTop: 8 }}>
              {!destination && (
                <div className="muted">
                  Tree and document writes are disabled until backups are set up.
                </div>
              )}
              {destination && !backupsOn && (
                <div className="muted">
                  {destination.status === "error"
                    ? `Setup didn't finish — ${destination.statusDetail ?? "verification failed"}. Writes are disabled.`
                    : "Setup didn't finish — the destination isn't verified. Writes are disabled."}
                </div>
              )}
              {backupsOn && incident.kind === "healthy" && (
                <div>
                  <div>Everything in sync — {who}.</div>
                  <div className="muted" style={{ marginTop: 4 }}>
                    Latest push {formatTimestamp(summary.latestLastSuccessAt)} · Oldest{" "}
                    {formatTimestamp(summary.oldestLastSuccessAt)}
                  </div>
                </div>
              )}
              {backupsOn && incident.kind === "empty" && (
                <div className="muted">No trees yet — they appear at their first write.</div>
              )}
              {backupsOn && incident.kind === "syncing" && (
                <div className="muted">
                  First sync in progress — {incident.done} of {incident.total} trees done. Reload to update.
                </div>
              )}
              {backupsOn && incident.kind === "destination" && (
                <div>
                  <div>
                    Nothing has pushed since {formatTimestamp(incident.lastPushAt)} — the backup box is not taking
                    pushes.
                  </div>
                  {incident.error && (
                    <div className="muted mono" style={{ marginTop: 4, fontSize: 12 }}>
                      {incident.error}
                    </div>
                  )}
                  <div className="muted" style={{ marginTop: 4 }}>
                    Retrying automatically. Writes continue; new work is unprotected until pushes resume.
                  </div>
                </div>
              )}
              {backupsOn && incident.kind === "partial" && (
                <div>
                  {incident.problems.length} of {summary.totalTrees} trees are behind while the rest push normally —
                  this should not happen; investigate now.
                </div>
              )}
            </div>

            {/* ── The rare partial-divergence alarm names exactly its trees ── */}
            {backupsOn && incident.kind === "partial" && (
              <div className="tableWrap" style={{ marginTop: 10 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Tree</th>
                      <th>Status</th>
                      <th>Failing</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incident.problems.map((row, i) => {
                      const sync = syncStatus(row);
                      const err = truncateError(row.lastError);
                      return (
                        <tr key={`${row.tenantName ?? "platform"}-${row.tree}-${i}`}>
                          <td>{tenantLabel(row.tenantName)}</td>
                          <td>{treeLabel(row.tree)}</td>
                          <td>{sync.pill ? <span className={`pill ${sync.pill}`}>{sync.label}</span> : sync.label}</td>
                          <td>{failingSince(row)}</td>
                          <td className="muted" title={err.truncated ? row.lastError ?? undefined : undefined}>
                            {err.text}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── Destination line + the rare gestures ────────────────────── */}
            <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {destination ? (
                <>
                  <span className="muted">
                    Backing up to <span className="mono">{destination.endpoint}</span>
                  </span>
                  <button
                    className="adminListAction"
                    type="button"
                    disabled={busy}
                    onClick={() => openFlow(destination.endpoint)}
                  >
                    {backupsOn ? "Change destination…" : "Resume setup…"}
                  </button>
                  <button className="adminListAction isDanger" type="button" disabled={busy} onClick={() => void remove()}>
                    Remove
                  </button>
                </>
              ) : (
                <button className="btn btnPrimary" type="button" disabled={busy} onClick={() => openFlow(lastEndpoint)}>
                  Set up backup…
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Setup / change flow (verification lives here) ────────────────── */}
        {!loading && !error && flowOpen && (
          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 10 }}>
              {destination ? "Change backup destination" : "Set up backup"}
            </div>

            {flow.step === "edit" && (
              <div style={{ display: "grid", gap: 10 }}>
                <label className="field">
                  <span className="label">Destination</span>
                  <input
                    className="input mono"
                    value={endpoint}
                    disabled={busy}
                    placeholder="ssh://atlas-backup@<box-ip>/srv/atlas-backup"
                    onChange={(e) => setEndpoint(e.target.value)}
                    onBlur={validateEndpoint}
                  />
                  {endpointError && <div className="help">{endpointError}</div>}
                </label>
                {endpoint.trim().length > 0 && endpointForm(endpoint) === "local" && (
                  <div>
                    <span className="pill pillWarn">Same-host placement — not a backup</span>
                    <div className="help">
                      A path on this server disappears with it. Fine for development; production uses the backup box.
                    </div>
                  </div>
                )}
                {flowError && <ErrorState detail={flowError} />}
                <div className="actions">
                  <button
                    className="btn btnPrimary"
                    type="button"
                    disabled={busy || endpoint.trim().length === 0 || endpointError !== null}
                    onClick={() => void connect()}
                  >
                    Connect &amp; turn on
                  </button>
                  <button className="btn" type="button" disabled={busy} onClick={() => setFlowOpen(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {flow.step === "connecting" && <div className="muted">Proving the connection — a probe push and read-back…</div>}

            {flow.step === "fingerprint" && (
              <div style={{ display: "grid", gap: 10 }}>
                <div>
                  First connection to this box. Its identity fingerprint is:
                </div>
                <input className="input mono" readOnly value={flow.fingerprint} />
                <div className="help">
                  Confirm it matches the box itself before continuing — this pins every future connection to that
                  machine.
                </div>
                <div className="actions">
                  <button
                    className="btn btnPrimary"
                    type="button"
                    disabled={busy}
                    onClick={() => void connect(flow.hostKeyLine)}
                  >
                    It matches — turn on backups
                  </button>
                  <button className="btn" type="button" disabled={busy} onClick={() => setFlowOpen(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
